import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CopyToClipboard } from 'shared/components/ui/ProjectIcons';
import {
  ApiPushDataType,
  CODE_LANGUAGES,
  generateSnippet,
  languageLabel,
  SnippetKind,
} from './snippets';
import { usePreferredCodeLanguage } from './usePreferredCodeLanguage';
import Alert from 'shared/components/ui/Alert';

const TOAST_DURATION_MS = 2000;

// Tabs, so they don't look like the API push panel's data-type picker above,
// which uses outlined buttons.
const tabBase =
  '-mb-px border-b-2 px-3 py-2 text-body-sm cursor-pointer transition-colors duration-200';
const tabActive = `${tabBase} border-ui-cta-primary text-ui-text-primary-text`;
const tabInactive = `${tabBase} border-transparent text-ui-text-secondary-text hover:text-ui-text-primary-text`;

type CodeSampleSwitcherProps = {
  endpoint: string;
  apiKey: string | null;
  integrationType: string;
  kind: SnippetKind;
  dataType?: ApiPushDataType;
};

const CodeSampleSwitcher = ({
  endpoint,
  apiKey,
  integrationType,
  kind,
  dataType,
}: CodeSampleSwitcherProps) => {
  const { t } = useTranslation();
  const [language, setLanguage] = usePreferredCodeLanguage();
  // Per instance, so two switchers on one page never share ids.
  const idPrefix = useId();
  const panelId = `${idPrefix}-panel`;
  const tabId = (lang: string) => `${idPrefix}-tab-${lang}`;

  const snippet = useMemo(
    () =>
      generateSnippet(language, {
        endpoint,
        apiKey,
        integrationType,
        kind,
        dataType,
      }),
    [language, endpoint, apiKey, integrationType, kind, dataType],
  );

  return (
    <div className="w-full">
      {apiKey !== null && (
        <Alert status="warning" className="mb-2 max-w-[765px]">
          {t(
            'This code contains your real API key. Avoid sharing your screen while this is visible.',
          )}
        </Alert>
      )}
      <div className="mb-3 flex flex-row items-center border-b border-ui-divider-lines">
        <div
          role="tablist"
          aria-label={t('Code language')}
          className="flex flex-row flex-wrap"
        >
          {CODE_LANGUAGES.map((lang, i) => {
            const isActive = lang === language;
            return (
              <button
                key={lang}
                type="button"
                role="tab"
                id={tabId(lang)}
                aria-controls={panelId}
                aria-selected={isActive}
                // One tab stop for the row; the arrow keys move between tabs.
                tabIndex={isActive ? 0 : -1}
                onClick={() => setLanguage(lang)}
                onKeyDown={(e) => {
                  const step =
                    e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
                  if (!step) return;
                  e.preventDefault();
                  const next =
                    CODE_LANGUAGES[
                      (i + step + CODE_LANGUAGES.length) % CODE_LANGUAGES.length
                    ];
                  setLanguage(next);
                  const sibling = (
                    step > 0
                      ? (e.currentTarget.nextElementSibling ??
                        e.currentTarget.parentElement?.firstElementChild)
                      : (e.currentTarget.previousElementSibling ??
                        e.currentTarget.parentElement?.lastElementChild)
                  ) as HTMLElement | null;
                  sibling?.focus();
                }}
                className={isActive ? tabActive : tabInactive}
              >
                {t(languageLabel(lang))}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard
              .writeText(snippet)
              .then(() =>
                toast.info(t('Code sample copied to clipboard'), {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: TOAST_DURATION_MS,
                }),
              )
              .catch(() =>
                toast.error(t('Failed to copy to clipboard'), {
                  position: toast.POSITION.TOP_RIGHT,
                  autoClose: TOAST_DURATION_MS,
                }),
              );
          }}
          className="ml-auto flex-shrink-0 text-ui-text-secondary-text hover:text-ui-text-primary-text"
          aria-label={t('Copy code sample')}
        >
          <CopyToClipboard />
        </button>
      </div>
      {/* Plain text in the same box as the endpoint, so the sample reads as
          one more value to copy rather than as an editor. */}
      <pre
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(language)}
        tabIndex={0}
        data-testid="CodeSampleSwitcher-code"
        className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-teal-t20 bg-teal-t10 px-4 py-3 font-mono text-body-sm text-ui-text-primary-text"
      >
        {snippet}
      </pre>
    </div>
  );
};

export default CodeSampleSwitcher;
